"use client";

/**
 * Dial Efficiency — call-EFFORT economics, benchmarked against the
 * "State of Outbound Telemarketing 2025" study (per-attempt payoff, cadence,
 * effort-per-appointment by demographic, effort-per-sale by state,
 * speed-to-lead). Methodology matches the per-agent Contact Rate by Attempt
 * card: 60s+ connects, multi-line race-loser cancels excluded.
 *
 * PER-AGENT MODE (agentId set) intentionally renders a REDUCED layout:
 * AgentDetailsView's hero strip already shows Dials/Production/Answer Rate,
 * and AgentPerformanceInsights already owns the attempt-number curve — so
 * this section shows only what nothing else does (benchmarks, cadence,
 * speed-to-lead, demographics, state economics). Don't re-add those here.
 */

import { Info, Loader2 } from "lucide-react";

import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/_module/components/ui/tooltip";

import { useGetDialEfficiency } from "../../hooks/use-hook";
import type { DialEffortSegment, DialEfficiencyData } from "../../services/services";

interface Props {
  agencyId: string;
  period?: '6-months' | '12-months' | 'year-to-date';
  startDate?: string;
  endDate?: string;
  /** When set: this agent's lead book, their own dials only. */
  agentId?: string;
  agentName?: string;
}

const fmtMoney = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
const fmtNum = (n: number) => new Intl.NumberFormat("en-US").format(n || 0);

const SPEED_BUCKETS: Array<{ key: string; label: string }> = [
  { key: "lte15m", label: "≤ 15 min" },
  { key: "lte1h", label: "15–60 min" },
  { key: "lte4h", label: "1–4 hrs" },
  { key: "lte24h", label: "4–24 hrs" },
  { key: "lte7d", label: "1–7 days" },
  { key: "gt7d", label: "> 7 days" },
  { key: "never", label: "Never called" },
];

/* ── section shell shared by every card so the tab reads as one system ── */
function Section({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="px-4 py-3 border-b border-gray-100">
        <h4 className="text-sm font-semibold text-gray-900 tracking-tight">{title}</h4>
        {sub && <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">{sub}</p>}
      </div>
      {children}
    </div>
  );
}

/* ── custom SVG bar chart for the attempt curve (agency mode only) ──
   Hand-drawn so it can carry the one thing a generic chart can't: the
   study's "keep calling through 9" guideline drawn into the plot. */
function AttemptCurveChart({ curve }: { curve: DialEfficiencyData["attemptCurve"] }) {
  const bars = curve.slice(0, 12);
  if (bars.length === 0) {
    return <p className="text-sm text-gray-400 py-8 text-center">No outbound dials recorded for these leads yet.</p>;
  }
  const W = 720;
  const H = 178;
  const padL = 8;
  const padR = 8;
  const chartTop = 18;
  const chartBottom = H - 34;
  const chartH = chartBottom - chartTop;
  const maxRate = Math.max(...bars.map((b) => b.connectRate), 1);
  const slot = (W - padL - padR) / 12;
  const barW = Math.min(34, slot * 0.56);
  const x = (i: number) => padL + slot * i + (slot - barW) / 2;
  const y = (rate: number) => chartTop + chartH * (1 - rate / maxRate);
  const guideX = bars.length >= 9 ? padL + slot * 9 : null;

  return (
    // min-w + overflow wrapper: on a phone the 720-unit canvas would shrink to
    // half size and the labels become unreadable — scroll sideways instead,
    // same convention as the dashboard's wide tables on iOS.
    <div className="overflow-x-auto" data-no-ptr>
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto min-w-[640px] text-primary" role="img"
      aria-label="Connect rate by attempt number">
      {/* baseline */}
      <line x1={padL} y1={chartBottom} x2={W - padR} y2={chartBottom} stroke="#e5e7eb" strokeWidth="1" />
      {/* study guideline after call 9 */}
      {guideX != null && (
        <g>
          <line x1={guideX} y1={chartTop - 6} x2={guideX} y2={chartBottom} stroke="#d1d5db" strokeWidth="1" strokeDasharray="3 4" />
          <text x={guideX - 5} y={chartTop + 2} textAnchor="end" fontSize="9" fill="#9ca3af">
            industry: value persists through call 9
          </text>
        </g>
      )}
      {bars.map((b, i) => {
        const bx = x(i);
        const by = y(b.connectRate);
        return (
          <g key={b.attempt}>
            <rect x={bx} y={by} width={barW} height={Math.max(chartBottom - by, 2)} rx="3"
              fill="currentColor" opacity={i < 9 ? 0.85 : 0.4} />
            <text x={bx + barW / 2} y={by - 5} textAnchor="middle" fontSize="10" fontWeight="600" fill="#111827">
              {b.connectRate}%
            </text>
            <text x={bx + barW / 2} y={chartBottom + 13} textAnchor="middle" fontSize="10" fill="#6b7280">
              {b.attempt}
            </text>
            <text x={bx + barW / 2} y={chartBottom + 25} textAnchor="middle" fontSize="8.5" fill="#b3b8c2">
              {fmtNum(b.leadsReached)}
            </text>
          </g>
        );
      })}
    </svg>
    </div>
  );
}

/* ── speed-to-lead distribution, drawn as one SVG so the bars, counts and
      reach-rates stay on a shared grid ── */
function SpeedChart({ data }: { data: DialEfficiencyData["speedToLead"] }) {
  const rows = SPEED_BUCKETS.map((b) => ({ ...b, row: data.find((r) => r.bucket === b.key) }));
  const maxLeads = Math.max(...data.map((r) => r.leads), 1);
  const W = 720;
  const rowH = 26;
  const H = rows.length * rowH + 6;
  const labelW = 92;
  const rateW = 92;
  const trackW = W - labelW - rateW - 16;

  return (
    <div className="overflow-x-auto" data-no-ptr>
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto min-w-[640px] text-primary" role="img" aria-label="Time from lead creation to first dial">
      {rows.map(({ key, label, row }, i) => {
        const yMid = i * rowH + rowH / 2 + 3;
        const w = Math.max(((row?.leads || 0) / maxLeads) * trackW, row?.leads ? 3 : 0);
        return (
          <g key={key}>
            <text x={labelW - 8} y={yMid + 3} textAnchor="end" fontSize="10" fill="#6b7280">{label}</text>
            <rect x={labelW} y={yMid - 7} width={trackW} height={14} rx="4" fill="#f3f4f6" />
            <rect x={labelW} y={yMid - 7} width={w} height={14} rx="4"
              fill={key === "never" ? "#fca5a5" : "currentColor"} opacity={key === "never" ? 1 : 0.85} />
            <text x={labelW + w + 6} y={yMid + 3} fontSize="10" fontWeight="600" fill="#111827">
              {fmtNum(row?.leads || 0)}
            </text>
            {row && key !== "never" && row.leads > 0 && (
              <text x={W - 4} y={yMid + 3} textAnchor="end" fontSize="9" fill="#9ca3af">
                {row.contactedPct}% reached
              </text>
            )}
          </g>
        );
      })}
    </svg>
    </div>
  );
}

function SegmentTable({ title, rows, note }: { title: string; rows: DialEffortSegment[]; note?: string }) {
  const shown = rows.filter((r) => r.leads > 0);
  const eligible = shown.filter((r) => r.appointments >= 3 && r.dials >= 50);
  const best = eligible.length
    ? eligible.reduce((a, b) => (b.apptsPer100Dials > a.apptsPer100Dials ? b : a))
    : null;
  return (
    <Section title={title} sub={note}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] text-gray-500 border-b border-gray-100">
              <th className="text-left font-medium px-4 py-2">Segment</th>
              <th className="text-right font-medium px-3 py-2">Leads</th>
              <th className="text-right font-medium px-3 py-2">Dials</th>
              <th className="text-right font-medium px-3 py-2">Appts</th>
              <th className="text-right font-medium px-3 py-2">Appts / 100 dials</th>
              <th className="text-right font-medium px-3 py-2">Dials per appt</th>
              <th className="text-right font-medium px-3 py-2">Policies Sold</th>
              <th className="text-right font-medium px-4 py-2">Premium Sold</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const isBest = best != null && r.band === best.band;
              return (
                <tr key={r.band} className={`border-b border-gray-50 last:border-0 ${isBest ? "bg-primary/5" : ""}`}>
                  <td className="px-4 py-2 font-medium text-gray-800 whitespace-nowrap">
                    {r.band}
                    {isBest && (
                      <span className="ml-2 text-[10px] font-semibold text-primary bg-primary/10 rounded px-1.5 py-0.5 align-middle">
                        lowest effort
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-600">{fmtNum(r.leads)}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{fmtNum(r.dials)}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{fmtNum(r.appointments)}</td>
                  <td className="px-3 py-2 text-right font-semibold text-gray-900">{r.apptsPer100Dials}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{r.dialsPerAppointment ?? "—"}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{fmtNum(r.policiesSold)}</td>
                  <td className="px-4 py-2 text-right text-gray-600 whitespace-nowrap">{fmtMoney(r.premium)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

export default function DialEfficiencyTab({ agencyId, period = "6-months", startDate, endDate, agentId, agentName }: Props) {
  const { data, isLoading, isError } = useGetDialEfficiency(agencyId, period, startDate, endDate, agentId);
  const isAgent = !!agentId;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-400">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Crunching call-effort data…
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="py-16 text-center text-sm text-gray-500">
        Couldn&apos;t load dial efficiency analytics. Try a different period or refresh.
      </div>
    );
  }

  const s = data.summary;

  // ---- benchmark computations vs the Outbound 2025 study ----
  const connectsWithin = (n: number) =>
    data.attemptCurve.slice(0, n).reduce((sum, a) => sum + a.connects, 0);
  const c3 = connectsWithin(3);
  const c9 = connectsWithin(9);
  const persistenceGain = c3 > 0 ? Math.round(((c9 - c3) / c3) * 100) : null; // study: +87%

  const apptIn = (keys: string[]) =>
    data.speedToLead.filter((r) => keys.includes(r.bucket)).reduce((sum, r) => sum + r.appointments, 0);
  const apptAll = data.speedToLead.reduce((sum, r) => sum + r.appointments, 0);
  const appt24Pct = apptAll > 0 ? Math.round((apptIn(["lte15m", "lte1h", "lte4h", "lte24h"]) / apptAll) * 100) : null;
  const appt7dPct = apptAll > 0 ? Math.round((apptIn(["lte15m", "lte1h", "lte4h", "lte24h", "lte7d"]) / apptAll) * 100) : null;

  // ≥50 dials, not just ≥20 leads: barely-dialed states produce near-zero
  // contact rates that blew the spread up to 100× in smoke testing.
  const spreadStates = data.byState.filter((r) => r.dials >= 50 && r.contactRate > 0);
  const stateSpread =
    spreadStates.length >= 2
      ? Math.round(
          (Math.max(...spreadStates.map((r) => r.contactRate)) /
            Math.min(...spreadStates.map((r) => r.contactRate))) * 10,
        ) / 10
      : null;

  const chip = (state: "ahead" | "close" | "behind" | "info" | "opportunity" | "balanced") => {
    const styles = {
      ahead: "bg-emerald-100 text-emerald-700",
      close: "bg-amber-100 text-amber-700",
      behind: "bg-red-100 text-red-600",
      info: "bg-gray-100 text-gray-500",
      // spread row: wide = budget to reallocate, tight = markets performing evenly
      opportunity: "bg-primary/10 text-primary",
      balanced: "bg-emerald-100 text-emerald-700",
    } as const;
    const labels = { ahead: "Ahead", close: "Close", behind: "Behind", info: "No data", opportunity: "Opportunity", balanced: "Balanced" } as const;
    return (
      <span className={`text-[10px] font-semibold rounded px-1.5 py-0.5 ${styles[state]}`}>{labels[state]}</span>
    );
  };
  const vs = (ours: number | null, theirs: number, closeBand = 10): "ahead" | "close" | "behind" | "info" | "opportunity" | "balanced" => {
    if (ours == null) return "info";
    if (ours >= theirs) return "ahead";
    if (ours >= theirs - closeBand) return "close";
    return "behind";
  };

  const benchmarkRows: Array<{ metric: string; ours: string; study: string; state: "ahead" | "close" | "behind" | "info" | "opportunity" | "balanced"; note: string }> = [
    {
      metric: "Extra conversations from calls 4–9",
      ours: persistenceGain == null ? "—" : `+${persistenceGain}%`,
      study: "+87%",
      state: persistenceGain == null ? "info" : persistenceGain >= 60 ? "ahead" : persistenceGain >= 30 ? "close" : "behind",
      note: "Low = leads retired after 2–3 tries.",
    },
    {
      metric: "Appointments set within 24h of first dial",
      ours: appt24Pct == null ? "—" : `${appt24Pct}%`,
      study: "59.6%",
      state: vs(appt24Pct, 59.6),
      note: "The benchmark's win event was handing the caller straight to an agent; ours is setting the appointment — compare the speed pattern, not the exact number.",
    },
    {
      metric: "Appointments set within 7 days",
      ours: appt7dPct == null ? "—" : `${appt7dPct}%`,
      study: "89.7%",
      state: vs(appt7dPct, 89.7),
      note: "",
    },
    {
      metric: "Leads getting 9+ dials in week one",
      ours: `${s.pctLeadsWith9PlusDials7d}%`,
      study: "100%",
      state: s.pctLeadsWith9PlusDials7d >= 50 ? "ahead" : s.pctLeadsWith9PlusDials7d >= 25 ? "close" : "behind",
      note: "The recommended playbook: every lead gets 9 dials in its first week. Most agencies stop at 1–2 calls.",
    },
    {
      metric: "Best-to-worst state spread (contact rate)",
      ours: stateSpread == null ? "—" : `${stateSpread}×`,
      study: "2.4×",
      // Not a pass/fail row: a wide spread is untapped budget-shifting upside,
      // a tight one means markets are performing evenly.
      state: stateSpread == null ? "info" : stateSpread > 3 ? "opportunity" : "balanced",
      note: "A wide spread = shift lead budget toward your strong states.",
    },
  ];

  const bestStates = [...data.byState]
    .filter((r) => r.policiesSold >= 2 && r.dialsPerSale != null)
    .sort((a, b) => a.dialsPerSale! - b.dialsPerSale!);
  const stateRows = [...data.byState].sort((a, b) => {
    if (a.dialsPerSale != null && b.dialsPerSale != null) return a.dialsPerSale - b.dialsPerSale;
    if (a.dialsPerSale != null) return -1;
    if (b.dialsPerSale != null) return 1;
    return b.dials - a.dials;
  });

  const speedHeadline =
    s.medianSpeedToLeadMinutes == null
      ? "—"
      : s.medianSpeedToLeadMinutes < 60
        ? `${s.medianSpeedToLeadMinutes} min`
        : s.medianSpeedToLeadMinutes < 60 * 24
          ? `${Math.round(s.medianSpeedToLeadMinutes / 6) / 10} hrs`
          : `${Math.round(s.medianSpeedToLeadMinutes / (60 * 2.4)) / 10} days`;

  return (
    <div className="space-y-5">
      {/* Header. Per-agent mode: the hero strip above already shows dial/production
          volume, so here a single cohort line replaces the tile row. */}
      <div>
        <h3 className="text-lg font-semibold tracking-tight">Dial Efficiency</h3>
        <p className="text-sm text-muted-foreground mt-0.5">
          {isAgent
            ? `${agentName || "This agent"}'s book, their own dials only — ${fmtNum(s.leads)} leads created in this period, ${fmtNum(s.dials)} dials, ${fmtNum(s.leadsWithAppointment)} appointments, ${fmtNum(s.policiesSold)} policies sold (${fmtMoney(s.premium)} premium).`
            : `What each appointment and premium sold costs in calling effort — across the ${fmtNum(s.leads)} leads created in this period.`}
        </p>
      </div>

      {/* No leads tile — the Performance Overview's "Total Leads" box up top
          already owns that number; the cohort count lives in the subtitle. */}
      {!isAgent && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
          {[
            { label: "Dials", value: fmtNum(s.dials), sub: `${fmtNum(s.connects)} connects (60s+)` as string | undefined },
            { label: "Contact rate", value: `${s.contactRate}%`, sub: `${fmtNum(s.leadsContacted)} leads reached` },
            { label: "Appointments", value: fmtNum(s.leadsWithAppointment), sub: "leads with an appt set" },
            {
              label: "Total Production",
              value: fmtMoney(s.productionPremium),
              sub: `${fmtMoney(s.premium)} sold & approved · ${fmtNum(s.policiesSold)} policies sold`,
              // Same-named overview tile counts premium by SALE date; this one
              // follows the LEADS created in the period (wherever their premium
              // landed) so effort and outcome stay on the same leads.
              info: "Premium produced by the leads created in this period, whenever it sold. The overview tile counts premium by its sale date instead — so the two can differ.",
            },
          ].map((t: { label: string; value: string; sub?: string; info?: string }) => (
            <div key={t.label} className="rounded-lg border border-gray-200 bg-white p-3">
              <div className="flex items-center gap-1 text-[11px] font-medium text-gray-500 leading-tight">
                {t.label}
                {t.info && (
                  <TooltipProvider>
                    <UITooltip>
                      <TooltipTrigger asChild>
                        <button type="button" aria-label="How this differs from the overview tile" className="inline-flex cursor-help items-center">
                          <Info className="h-3 w-3 text-primary/70 transition-colors hover:text-primary" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-64 text-left text-xs leading-snug">
                        {t.info}
                      </TooltipContent>
                    </UITooltip>
                  </TooltipProvider>
                )}
              </div>
              <div className="text-lg font-semibold text-gray-900 mt-0.5">{t.value}</div>
              {t.sub && <div className="text-[11px] text-gray-400 mt-0.5 leading-tight">{t.sub}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Benchmark scorecard */}
      <Section
        title="Your numbers vs. industry outbound benchmarks"
        sub="Your life-insurance data for this period, against published outbound insurance calling benchmarks — read the benchmark column directionally."
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] text-gray-500 border-b border-gray-100">
                <th className="text-left font-medium px-4 py-2">Metric</th>
                <th className="text-right font-medium px-3 py-2">Us</th>
                <th className="text-right font-medium px-3 py-2">Benchmark</th>
                <th className="text-center font-medium px-3 py-2">Verdict</th>
              </tr>
            </thead>
            <tbody>
              {benchmarkRows.map((r) => (
                <tr key={r.metric} className="border-b border-gray-50 last:border-0">
                  <td className="px-4 py-2">
                    <span className="font-medium text-gray-800">{r.metric}</span>
                    {r.note && <span className="ml-2 text-[11px] text-gray-400">{r.note}</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-gray-900 whitespace-nowrap">{r.ours}</td>
                  <td className="px-3 py-2 text-right text-gray-500 whitespace-nowrap">{r.study}</td>
                  <td className="px-3 py-2 text-center">{chip(r.state)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Headline effort cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-sm font-semibold text-gray-900">Follow-up pace, first 7 days</div>
          <div className="mt-1.5 text-2xl font-bold text-gray-900">{s.avgDialsFirst7Days}</div>
          <div className="text-xs text-gray-500">avg dials per lead in its first week</div>
          <div className="mt-2.5 flex items-baseline gap-2">
            <span className="text-lg font-semibold text-gray-900">{s.pctLeadsWith9PlusDials7d}%</span>
            <span className="text-xs text-gray-500">of leads get 9+ dials in week one</span>
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-sm font-semibold text-gray-900">Speed to lead</div>
          <div className="mt-1.5 text-2xl font-bold text-gray-900">{speedHeadline}</div>
          <div className="text-xs text-gray-500">median time from lead created to first dial</div>
          <p className="text-[11px] text-gray-400 mt-2 leading-snug">
            Intent decays fastest in the first 6–12 hours — target minutes, not hours.
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-sm font-semibold text-gray-900">Appointment effort</div>
          <div className="mt-1.5 text-2xl font-bold text-gray-900">
            {apptAll > 0 && s.leadsWithAppointment > 0 && s.dials > 0
              ? `${Math.round((s.dials / s.leadsWithAppointment) * 10) / 10}`
              : "—"}
            <span className="text-xs font-normal text-gray-500 ml-1.5">dials per appointment</span>
          </div>
          <div className="mt-2.5 flex items-baseline gap-2">
            <span className="text-lg font-semibold text-gray-900">
              {(() => {
                const within9 = data.attemptsToAppointment
                  .filter((r) => r.attempts <= 9)
                  .reduce((sum, r) => sum + r.appointments, 0);
                const tot = data.attemptsToAppointment.reduce((sum, r) => sum + r.appointments, 0);
                return tot > 0 ? `${Math.round((within9 / tot) * 100)}%` : "—";
              })()}
            </span>
            <span className="text-xs text-gray-500">land within the first 9 dials</span>
          </div>
        </div>
      </div>

      {/* Attempt curve — agency mode only. In per-agent mode this would
          duplicate the Contact Rate by Attempt card in Performance Insights. */}
      {!isAgent && (
        <Section
          title="Connect rate by attempt number"
          sub="Share of that attempt's dials that became a 60s+ conversation, with leads still in play under each call."
        >
          <div className="p-4">
            <AttemptCurveChart curve={data.attemptCurve} />
          </div>
        </Section>
      )}

      {/* Demographics */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <SegmentTable
          title="Appointments per effort, by age band"
          rows={data.byAge}
          note="Higher appts-per-100-dials = this demographic books with less calling effort."
        />
        <SegmentTable title="By gender" rows={data.byGender} />
      </div>

      {/* State economics */}
      <Section
        title="State economics — what selling costs by state"
        sub="Sorted by dials per policy sold (lower = sells with less effort). States with under 5 leads hidden."
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] text-gray-500 border-b border-gray-100">
                <th className="text-left font-medium px-4 py-2">State</th>
                <th className="text-right font-medium px-3 py-2">Leads</th>
                <th className="text-right font-medium px-3 py-2">Dials</th>
                <th className="text-right font-medium px-3 py-2">Contact %</th>
                <th className="text-right font-medium px-3 py-2">Appts</th>
                <th className="text-right font-medium px-3 py-2">Policies Sold</th>
                <th className="text-right font-medium px-3 py-2">Dials / policy sold</th>
                <th className="text-right font-medium px-4 py-2">Premium Sold / 100 dials</th>
              </tr>
            </thead>
            <tbody>
              {stateRows.map((r, i) => {
                const isTop = bestStates.slice(0, 3).some((b) => b.state === r.state);
                return (
                  <tr key={`${r.state}-${i}`} className={`border-b border-gray-50 last:border-0 ${isTop ? "bg-primary/5" : ""}`}>
                    <td className="px-4 py-2 font-medium text-gray-800 whitespace-nowrap">
                      {r.state}
                      {isTop && (
                        <span className="ml-2 text-[10px] font-semibold text-primary bg-primary/10 rounded px-1.5 py-0.5 align-middle">
                          best ROI
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-600">{fmtNum(r.leads)}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{fmtNum(r.dials)}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{r.contactRate}%</td>
                    <td className="px-3 py-2 text-right text-gray-600">{fmtNum(r.appointments)}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{fmtNum(r.policiesSold)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-900">{r.dialsPerSale ?? "—"}</td>
                    <td className="px-4 py-2 text-right text-gray-600 whitespace-nowrap">{fmtMoney(r.premiumPer100Dials)}</td>
                  </tr>
                );
              })}
              {stateRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-400">
                    Not enough state data in this period yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Speed to lead distribution */}
      <Section
        title="Speed to first dial"
        sub="How fast new leads get their first call, and how reach falls off as leads sit."
      >
        <div className="p-4">
          <SpeedChart data={data.speedToLead} />
        </div>
      </Section>
    </div>
  );
}
